// @ts-check
// pwa/components/keyboard-shortcuts.js — the Hub's global keyboard shortcuts.
//
// One window-level keydown listener (bubble phase, so every component's own document /
// element handler runs first and can claim a key with preventDefault). Pure key logic is
// in lib/shortcuts.js; this file does the DOM side:
//
//   Cmd/Ctrl+K and /  The Hub has no global search. Focus this page's own search box
//                     (marked [data-page-search]); on a page without one, open
//                     Resources › All documents and focus its search, the one search
//                     every signed-in person can use.
//   ?                 The "Keyboard shortcuts" dialog (APG dialog: focus trap, Esc,
//                     focus goes back to where it was).
//   g then a letter   Go to Dashboard / Sign up / Resources / History; Admin and
//                     Admin › Learn only for people who can open them.
//   Esc               Fallback for dialogs, menus and drawers that don't close on Esc
//                     by themselves: activates the top visible [data-esc-close] control
//                     (a backdrop, close button or open menu toggle).
//
// Signed-out screens get no shortcuts. Nothing but Esc fires while typing. While another
// modal is open only Esc is handled.

import { html, render, nothing } from 'lit-html';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { icon } from '../lib/icons.js';
import { $session, hasAdminArea } from '../lib/store.js';
import { hasLearnAccess } from '../lib/learn-access.js';
import {
  resolveShortcut, isTypingTarget, isMacPlatform, GO_TARGETS, canGo, shortcutGroups,
} from '../lib/shortcuts.js';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
/** Where Cmd+K goes when the current page has no search box of its own. */
export const FALLBACK_SEARCH_PATH = '/resources/all';

let installed = false;
let seq = { pending: /** @type {string|null} */ (null), pendingAt: 0 };
/** @type {HTMLElement|null} */ let host = null;
/** @type {HTMLElement|null} */ let opener = null;
/** @type {Element[]} */ let inerted = [];

export function isMac() {
  const nav = /** @type {any} */ (globalThis.navigator);
  return isMacPlatform(nav?.userAgentData?.platform || nav?.platform || '');
}

function access(me) {
  return { adminArea: hasAdminArea(me), learn: hasLearnAccess(me) };
}

/** Install the listener once (app boot). */
export function installKeyboardShortcuts() {
  if (installed) return;
  installed = true;
  window.addEventListener('keydown', onKeydown);
}

/** Shown, laid out and on screen (closed sheets sit off-screen or display:none). */
function isShown(el) {
  if (!(el instanceof HTMLElement) || !el.isConnected) return false;
  if (el.closest('[inert], [hidden], [aria-hidden="true"]')) return false;
  const rects = el.getClientRects();
  if (!rects.length) return false;
  const cs = getComputedStyle(el);
  if (cs.visibility === 'hidden' || cs.pointerEvents === 'none' || Number(cs.opacity) === 0) return false;
  const r = el.getBoundingClientRect();
  return r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
}

/** Another modal (not ours) is open: only Esc is ours to handle then. */
function otherModalOpen() {
  if (document.querySelector('dialog[open]')) return true;
  if (document.querySelector('.cm-layer.active')) return true;   // Hub Tips tour
  return Array.from(document.querySelectorAll('[aria-modal="true"]')).some(isShown);
}

/** @param {KeyboardEvent} e */
function onKeydown(e) {
  if (e.defaultPrevented) return;
  if (host) return;                                  // our dialog handles its own keys
  const me = $session.get();
  if (!me) return;                                   // signed-out / public screens
  const r = resolveShortcut(e, {
    mac: isMac(), typing: isTypingTarget(e.target),
    pending: seq.pending, pendingAt: seq.pendingAt, now: Date.now(),
  });
  seq = { pending: r.pending, pendingAt: r.pendingAt };
  if (!r.action || r.action === 'pending') return;
  if (r.action === 'escape') { escFallback(e); return; }
  if (otherModalOpen()) return;

  if (r.action === 'search') { e.preventDefault(); openSearch(); return; }
  if (r.action === 'help') { e.preventDefault(); openShortcutsDialog(); return; }
  if (r.action.startsWith('go:')) {
    const path = r.action.slice(3);
    const target = GO_TARGETS.find((t) => t.path === path);
    if (!target || !canGo(target, access(me))) return;
    e.preventDefault();
    navigate(path);
  }
}

// ── Search ────────────────────────────────────────────────────────────────

function pageSearch() {
  return /** @type {HTMLInputElement|undefined} */ (
    Array.from(document.querySelectorAll('#app-main [data-page-search]')).find(isShown));
}

function focusBox(box) {
  box.focus();
  try { box.select?.(); } catch { /* some input types can't select */ }
}

/** Focus this page's search, else open Resources › All documents and focus that. */
export function openSearch() {
  const box = pageSearch();
  if (box) { focusBox(box); return; }
  const main = document.getElementById('app-main');
  if (main) {
    const obs = new MutationObserver(() => {
      const b = pageSearch();
      if (b) { obs.disconnect(); clearTimeout(t); focusBox(b); }
    });
    obs.observe(main, { childList: true, subtree: true });
    const t = setTimeout(() => obs.disconnect(), 6000);
  }
  navigate(FALLBACK_SEARCH_PATH);
}

/** In-app navigation through the router (it intercepts [data-nav] anchor clicks). */
function navigate(path) {
  const a = document.createElement('a');
  a.href = path;
  a.setAttribute('data-nav', '');
  a.hidden = true;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ── Esc fallback ──────────────────────────────────────────────────────────

/** Stacking order of an element: the highest z-index among its positioned ancestors. */
function zOf(el) {
  let z = 0;
  for (let n = el; n && n instanceof HTMLElement; n = n.parentElement) {
    const v = parseInt(getComputedStyle(n).zIndex, 10);
    if (!Number.isNaN(v)) z = Math.max(z, v);
  }
  return z;
}

/** @param {KeyboardEvent} e */
function escFallback(e) {
  if (document.querySelector('dialog[open]')) return;          // the browser closes it
  const live = Array.from(document.querySelectorAll('[data-esc-close]')).filter(isShown);
  if (!live.length) return;
  let top = live[0]; let topZ = zOf(top);
  for (const el of live.slice(1)) { const z = zOf(el); if (z >= topZ) { top = el; topZ = z; } }
  e.preventDefault();
  /** @type {HTMLElement} */ (top).click();
  // A menu toggle stays on the page: keep focus on it so keyboard users aren't dropped.
  if (top.isConnected && top.matches('[aria-expanded]') && (document.activeElement === document.body || !document.activeElement)) {
    /** @type {HTMLElement} */ (top).focus();
  }
}

// ── "Keyboard shortcuts" dialog ───────────────────────────────────────────

/** Keys for one row. Real spaces between parts so screen readers say "Ctrl K or /",
 *  "g then d" (gaps between keys come from the flex gap, so the text spaces are free). */
function kbdKeys(keys, seqKeys) {
  return keys.map((alt, i) => html`${i ? html` <span class="kbd-or">or</span> ` : nothing}<span class="kbd-combo">${alt.map((k, j) => html`${j ? (seqKeys ? html` <span class="kbd-then">then</span> ` : ' ') : nothing}<kbd>${k}</kbd>`)}</span>`);
}

function dialogTemplate(me) {
  const groups = shortcutGroups({ mac: isMac(), ...access(me) });
  return html`
    <div class="modal-backdrop is-open kbd-backdrop" data-kbd-backdrop>
      <div class="modal kbd-modal" role="dialog" aria-modal="true" aria-labelledby="kbd-title" aria-describedby="kbd-note">
        <div class="kbd-head">
          <h2 id="kbd-title" class="modal__title">Keyboard shortcuts</h2>
          <button type="button" class="kbd-close" data-kbd-close aria-label="Close">${unsafeHTML(icon('x', 'w-5 h-5'))}</button>
        </div>
        ${groups.map((g) => html`
          <section class="kbd-group" aria-labelledby=${`kbd-g-${g.group.replace(/\W+/g, '').toLowerCase()}`}>
            <h3 class="kbd-group__title" id=${`kbd-g-${g.group.replace(/\W+/g, '').toLowerCase()}`}>${g.group}</h3>
            <dl class="kbd-list">
              ${g.rows.map((r) => html`<div class="kbd-row"><dt>${r.label}</dt><dd>${kbdKeys(r.keys, r.seq)}</dd></div>`)}
            </dl>
          </section>`)}
        <p class="modal__body kbd-note" id="kbd-note">Shortcuts pause while you're typing in a box. Esc always works.</p>
      </div>
    </div>`;
}

/** @param {KeyboardEvent} e */
function onDialogKey(e) {
  if (!host) return;
  if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeShortcutsDialog(); return; }
  if (e.key !== 'Tab') return;
  const dlg = host.querySelector('[role="dialog"]');
  if (!dlg) return;
  const f = /** @type {HTMLElement[]} */ (Array.from(dlg.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0));
  if (!f.length) { e.preventDefault(); return; }
  const first = f[0]; const last = f[f.length - 1];
  const inside = dlg.contains(document.activeElement);
  if (e.shiftKey && (document.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && (document.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
}

export function isShortcutsDialogOpen() { return !!host; }

/** Open the dialog. Focus goes to its close button; it returns to `returnTo` (or the
 *  element focused now) on close. */
export function openShortcutsDialog(returnTo) {
  const me = $session.get();
  if (!me || host) return;
  opener = /** @type {HTMLElement|null} */ (returnTo || document.activeElement);
  host = document.createElement('div');
  host.id = 'kbd-shortcuts';
  document.body.appendChild(host);
  render(dialogTemplate(me), host);
  host.addEventListener('click', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.matches('[data-kbd-backdrop]') || t.closest('[data-kbd-close]')) closeShortcutsDialog();
  });
  document.addEventListener('keydown', onDialogKey, true);
  // Keep the page behind out of the tab order and the accessibility tree.
  inerted = Array.from(document.body.children)
    .filter((el) => el !== host && !el.hasAttribute('inert') && !['SCRIPT', 'STYLE', 'TEMPLATE'].includes(el.tagName));
  inerted.forEach((el) => el.setAttribute('inert', ''));
  /** @type {HTMLElement|null} */ (host.querySelector('[data-kbd-close]'))?.focus();
}

export function closeShortcutsDialog() {
  if (!host) return;
  document.removeEventListener('keydown', onDialogKey, true);
  inerted.forEach((el) => el.removeAttribute('inert'));
  inerted = [];
  render(nothing, host);
  host.remove();
  host = null;
  const back = opener; opener = null;
  if (back && back.isConnected && back !== document.body) back.focus();
}
